// Consistent formatting class mapping
export const CONTROLLED_FORMATTING_CLASSES = {
  "font-bold": "bold",
  italic: "italic",
  underline: "underline",
  "line-through": "strikethrough",
} as const;

export type FormattingState = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
};

type StyleOverrides = {
  fontWeight?: number | null;
  fontStyle?: string | null;
  textDecorationLine?: string | null;
};

/**
 * Parse existing text formatting from styleOverrides and className
 * Uses computed styles where available, falls back to className parsing
 * @param styleOverrides - Computed styles from the element
 * @param className - CSS class names from the element
 * @returns Object with boolean values for each formatting state
 */
export function parseTextFormatting(
  styleOverrides: StyleOverrides = {},
  className: string = "",
): FormattingState {
  const classes = new Set(
    className.split(" ").filter((cls: string) => cls.trim() !== ""),
  );
  const activeFormatting: Record<string, boolean> = {};

  // Use styleOverrides for properties that are available there
  // Bold detection from fontWeight (700+ is considered bold)
  activeFormatting.bold = (styleOverrides?.fontWeight ?? 400) >= 700;

  // Italic detection from fontStyle
  activeFormatting.italic = styleOverrides?.fontStyle === "italic";

  // Text decoration detection from computed styles
  const textDecorationLine = styleOverrides?.textDecorationLine;
  if (textDecorationLine) {
    const decorationLines = new Set(
      textDecorationLine.split(" ").map((line: string) => line.trim()),
    );
    activeFormatting.underline = decorationLines.has("underline");
    activeFormatting.strikethrough = decorationLines.has("line-through");
  }

  // Fall back to className parsing for any properties not detected from styleOverrides
  Object.keys(CONTROLLED_FORMATTING_CLASSES).forEach((cssClass) => {
    const formatKey =
      CONTROLLED_FORMATTING_CLASSES[
        cssClass as keyof typeof CONTROLLED_FORMATTING_CLASSES
      ];

    // Only set from className if not already set from styleOverrides
    if (!(formatKey in activeFormatting)) {
      activeFormatting[formatKey] = classes.has(cssClass);
    }
  });

  // Override with className if present (className takes precedence)
  if (classes.has("font-bold")) {
    activeFormatting.bold = true;
  }
  if (classes.has("italic")) {
    activeFormatting.italic = true;
  }
  if (classes.has("underline")) {
    activeFormatting.underline = true;
  }
  if (classes.has("line-through")) {
    activeFormatting.strikethrough = true;
  }

  return {
    bold: activeFormatting.bold ?? false,
    italic: activeFormatting.italic ?? false,
    underline: activeFormatting.underline ?? false,
    strikethrough: activeFormatting.strikethrough ?? false,
  };
}

/**
 * Build className string from formatting state
 * @param formatting - The formatting state object
 * @returns Space-separated CSS class names
 */
export function buildFormattingClassName(formatting: FormattingState): string {
  const formattingClasses: string[] = [];

  if (formatting.bold) {
    formattingClasses.push("font-bold");
  }
  if (formatting.italic) {
    formattingClasses.push("italic");
  }
  if (formatting.underline) {
    formattingClasses.push("underline");
  }
  if (formatting.strikethrough) {
    formattingClasses.push("line-through");
  }

  return formattingClasses.join(" ");
}
